import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface SecurityDashboard {
  readonly incidents: { open: number; critical: number; high: number; slaBreached: number };
  readonly risks: { open: number; critical: number; high: number; pendingExecReview: number };
  readonly vulnerabilities: { open: number; critical: number; high: number; slaBreached: number };
  readonly compliance: { frameworks: number; averageScore: number };
  readonly threats: { active: number; alerts: number };
  readonly privacy: { open: number; slaBreached: number };
  readonly assets: { total: number; critical: number; high: number };
  readonly generatedAt: string;
}

/**
 * M30 — Security Operations Dashboard (§22 / §31). Single-glance executive view
 * of the enterprise security posture: open incidents, risks pending executive
 * review (BR-03), unremediated vulnerabilities, compliance health, active
 * threats + alerts (BR-05), outstanding privacy requests and the critical-asset
 * footprint — aggregated live across every M30 domain.
 */
export function M30SecurityDashboardPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [data, setData] = useState<SecurityDashboard | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    apiRequest<{ data: SecurityDashboard }>('/v1/security/dashboard', { token })
      .then((r) => { if (alive) { setData(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token]);

  return (
    <main aria-label="security-dashboard">
      <h1>{t('m30.dashboard.title')}</h1>
      {error && <p role="alert">{error}</p>}
      {loading || !data ? (
        <p>{t('m30.dashboard.loading')}</p>
      ) : (
        <section aria-label="posture-cards" className="card-grid">
          <article aria-label="card-incidents">
            <h2>{t('m30.dashboard.cardIncidents')}</h2>
            <p aria-label="incidents-open">{t('m30.dashboard.open', { count: data.incidents.open })}</p>
            <p aria-label="incidents-critical">{t('m30.dashboard.critical', { count: data.incidents.critical })}</p>
            <p aria-label="incidents-sla">{t('m30.dashboard.slaBreached', { count: data.incidents.slaBreached })}</p>
          </article>
          <article aria-label="card-risks">
            <h2>{t('m30.dashboard.cardRisks')}</h2>
            <p aria-label="risks-open">{t('m30.dashboard.open', { count: data.risks.open })}</p>
            <p aria-label="risks-critical">{t('m30.dashboard.critical', { count: data.risks.critical })}</p>
            <p aria-label="risks-exec-review">{t('m30.dashboard.pendingExecReview', { count: data.risks.pendingExecReview })}</p>
          </article>
          <article aria-label="card-vulnerabilities">
            <h2>{t('m30.dashboard.cardVulnerabilities')}</h2>
            <p aria-label="vulns-open">{t('m30.dashboard.open', { count: data.vulnerabilities.open })}</p>
            <p aria-label="vulns-critical">{t('m30.dashboard.critical', { count: data.vulnerabilities.critical })}</p>
            <p aria-label="vulns-sla">{t('m30.dashboard.slaBreached', { count: data.vulnerabilities.slaBreached })}</p>
          </article>
          <article aria-label="card-compliance">
            <h2>{t('m30.dashboard.cardCompliance')}</h2>
            <p aria-label="compliance-frameworks">{t('m30.dashboard.frameworks', { count: data.compliance.frameworks })}</p>
            <p aria-label="compliance-score">{t('m30.dashboard.avgScore', { score: data.compliance.averageScore })}</p>
          </article>
          <article aria-label="card-threats">
            <h2>{t('m30.dashboard.cardThreats')}</h2>
            <p aria-label="threats-active">{t('m30.dashboard.active', { count: data.threats.active })}</p>
            <p aria-label="threats-alerts">{t('m30.dashboard.alerts', { count: data.threats.alerts })}</p>
          </article>
          <article aria-label="card-privacy">
            <h2>{t('m30.dashboard.cardPrivacy')}</h2>
            <p aria-label="privacy-open">{t('m30.dashboard.open', { count: data.privacy.open })}</p>
            <p aria-label="privacy-sla">{t('m30.dashboard.slaBreached', { count: data.privacy.slaBreached })}</p>
          </article>
          <article aria-label="card-assets">
            <h2>{t('m30.dashboard.cardAssets')}</h2>
            <p aria-label="assets-total">{t('m30.dashboard.total', { count: data.assets.total })}</p>
            <p aria-label="assets-critical">{t('m30.dashboard.critical', { count: data.assets.critical })}</p>
          </article>
        </section>
      )}
    </main>
  );
}
